<script lang="ts" setup>
import { ref, reactive, computed, provide, onUnmounted, watch, nextTick } from 'vue';
import { useLocalization } from '@/plugins/localization';
import { useEvents } from '@/plugins/events';
import { useAlerts } from '@/plugins/alerts';
import { useLowCodeStore } from '@/modules/low-code/common/store/low-code';
import Pager from '@/helpers/Pager';
import { Form } from '@/helpers/Form';
import { FormContract, FormEntry } from '@/components/forms/blueprints/logito-form/index';
import { RelatedDocumentContract, RelatedDocumentEntry } from '@/components/forms/blueprints/logito-related-documents';
import { EntryFactory } from '@/components/builder/form/traits/EntryFactory';
import { FluentFormBuilder } from '@/components/forms';
import { AttachmentsContract, instanceOfAttachmentsEntry } from '@/components/forms/blueprints/attachments';
import { instanceOfUserGroupEntry } from '@/components/forms/blueprints/logito-user-group';
import { instanceOfUserEntry } from '@/components/forms/blueprints/logito-user';
import { Blueprint } from '@/components/builder/base/blueprints/Blueprint';
import { Entry } from '@/components/builder/form/entries/Entry';
import { FrontActionModal } from '@/components/common/dynamic-grid/helpers/GridEnums';
import ModulesService from '@/modules/low-code/services/ModulesService';
import BpmnService, { BpmnEmitOptions } from '@/modules/low-code/process/services/BpmnService';
import StorageService, { FilePreview, Resource as FileResource } from '@/modules/core/files/services/StorageService';
import FileReader from '@/views/partials/components/attachments/FIleReader.vue';
import IdeoModal from '@/components/ideo/modal/IdeoModal.vue';
import Loader from '@/components/common/Loader.vue';
import { inject } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import GroupExecutionProgress from '@/modules/low-code/components/modals/process/GroupExecutionProgress.vue';
import { FormBuilderContract } from '@/components/builder/form';
import TriggersService from '@/modules/low-code/services/TriggersService';
import { TriggerModel, TriggerModes, TriggerMoments } from '@/components/builder/form/traits/HasTriggers';
import { emitBlueprintChanges, runTrigger, useDynamicTrigger } from '@/components/forms/helpers/DynamicTriggerHelper';
import { useControlReload } from '@/components/forms/helpers/RelatedControlsHelper';
import { useAuth } from '@/plugins/auth';

const emit = defineEmits(["reload"]);

const props = defineProps({
  "level": null
});

const actions: any = inject('actions');

const { $t } = useLocalization();
const { $alert } = useAlerts();
const { $events } = useEvents();
const lowCodeStore = useLowCodeStore();
const store = useStore();
const { $auth } = useAuth();

const formObj = ref<HTMLElement | null>(null);
const form = reactive(Form.create({
    document: null as FormContract,
}));

const entries = ref<Record<string, FormEntry>>({});
const builders = ref<Record<string, FormBuilderContract>>({});
const documentTitles = ref<Record<string, string>>({});

const modal = ref<InstanceType<typeof IdeoModal> | null>(null);
const exec = ref<InstanceType<typeof GroupExecutionProgress>>(null);

const isExecuting = ref(false);
const executionStateId = ref<string | null>(null);

const reloadKey = ref(0);
const isLoaded = ref(false);
const filePreview = ref<{ file: FileResource, documentControlName: string}[]>([]);
const hasRunTrigger = ref(false);
const isSubprocess = ref(false);

const action = reactive(Form.create<BpmnEmitOptions>({
    id: '',
    licence: '',
    actionName: '',
    buttonId: '',
    displayMode: 'Modal',
    displaySize: 'Medium',
    title: $t('[[[Formularz]]]'),
    buttonName: $t('[[[Zapisz]]]'),
    buttonVariant: 'success',
    multiformMode: false,
    buttonIcon: '',
    groupAction: false,
    formButtonName: '',
    blueprint: null,
}));

provide('filePreview', filePreview);
provide('level', props.level);

const formId = computed(() => entries.value[guids.value?.[0] ?? '']?.formId ?? '');

const isLogitoDocument = computed(() =>
{
    const document = builders.value[guids.value[0]]?.schema?.names('document');

    if (document?.length && action.displaySize === 'LargeWithDocument') return true;

    return false;
});

const size = computed(() =>
{
    switch (action.displaySize)
    {
        case 'Large':
            return 'xl';
        case 'LargeWithDocument':
            return 'xl';
        case 'Medium':
            return 'lg';
        case 'Small':
            return 'md';
        default:
            return 'md';
    }
});

const title = computed(() => form.document?.title ? builders.value[guids.value[0]]?.localization.translate(form.document.title) : action.name);
const guids = computed<string[]>(() => action.id.split(','));

watch(
    () => formObj?.value,
    async (val) =>
    {
        if (val && !hasRunTrigger.value)
        {
            await new Promise(resolve => setTimeout(resolve, 200));

            const fullId = guids.value ? guids.value + '/' + action.buttonId : action.buttonId;

            await runTrigger(TriggerMoments.onLoad, form.document, null, builders.value[guids.value[0]], builders.value[guids.value[0]], action.licence, fullId, reloadDataWithoutReloadForm, $alert, entries.value[guids.value[0] ?? ''], formObj, props, $events);
            hasRunTrigger.value = true;
        }
    }
);

watch(() => action.actionName, () =>
{
    const fullActionName = action.buttonId ? `${action.actionName}-${action.buttonId}` : action.actionName;

    if (!fullActionName) return;

    const foundValue = actions?.value[fullActionName] ?? store.state?.actions[fullActionName] ?? null;

    if (foundValue?.formButtonName)
    {
        action.formButtonName = foundValue.formButtonName;
    }
});

const reset = () =>
{
    if (guids.value?.[0] && builders.value[guids.value[0]]?.blueprint?.id)
    {
        $events.$off(`form-reload-data-${builders.value[guids.value[0]].blueprint.id}`);
        useDynamicTrigger($events, builders.value[guids.value[0]].blueprint.id).eventOff();
    }

    isLoaded.value = false;
    entries.value = {};
    builders.value = {};
    form.document = null;
    action.reset();
    hasRunTrigger.value = false;
};

const loadForm = async () =>
{
    try
    {
        const response = guids.value.length > 0 && guids.value[0]
            ? await ModulesService.fetchForm(`${action.licence}/${guids.value[0]}/${action.actionName}/${action.buttonId}`)
            : await ModulesService.fetchForm(`${action.licence}/bpmn-start/${action.buttonId}`);

        form.document = response;

        const guidsToLoad = action.multiformMode ? guids.value : [guids.value[0] ?? ''];

        for (const guid of guidsToLoad)
        {
            entries.value[guid] = new FormEntry();

            const builder: FormBuilderContract = FluentFormBuilder
                .setup(false, true)
                .blueprint(() => form.document, () => 0)
                .entry(() => entries.value[guid], () => 0)
                .make();

            builder.document.isEdit = guids.value != null && guids.value != undefined && guids.value[0] != '';
            builder.document.user = $auth.user();
            builder.update();
            builders.value[guid] = builder;
        }

        filePreview.value = [];
    }
    catch (ex: any)
    {
        if (ex.code === 400) $alert.warning(ex.message);
    }
};

const loadData = async () =>
{
    if (action.groupAction && !action.multiformMode) return;

    try
    {
        if (guids.value && guids.value.length > 0 && guids.value[0])
        {
            for (const guid of guids.value)
            {
                const response = await BpmnService.fetchFormEntry(
                    action.licence,
                    guid,
                    action.buttonId
                );

                entries.value[guid] = response;
                builders.value[guid]?.update();

                documentTitles.value[guid] = response._documentTitle?.data ?? '';
            }

            return;
        }

        if (!lowCodeStore.fieldKey || !lowCodeStore.document)
            return;

        const guid = guids.value?.[0] ?? '';
        const builder = guid != null ? builders.value[guid] : null;

        if (!builder) return;

        entries.value[guid] = entries.value[guid] ?? new FormEntry();
        entries.value[guid][lowCodeStore.fieldKey] = new RelatedDocumentEntry();
        entries.value[guid][lowCodeStore.fieldKey].data = [lowCodeStore.document];
        builder.document.parentDocument = lowCodeStore.document;
        builder.document.isSubProcess = true;
        isSubprocess.value = true;

        const logitoRelatedDocumentsBlueprint = builder.schema.find(
            lowCodeStore.fieldKey
        ) as RelatedDocumentContract;

        if (!logitoRelatedDocumentsBlueprint) return;

        Object.entries(logitoRelatedDocumentsBlueprint.fieldMappings).forEach(([key, value]) =>
        {
            const blueprint = builder.schema.find(key) as EntryFactory<any>;

            if (!blueprint) return;

            entries.value[guids.value[0] ?? ''][key] = blueprint.createEntry(lowCodeStore.document.additionalFields?.[value]?.data);
        });

        builder.update();

    }
    catch (ex: any)
    {
        if (ex.code === 400) $alert.warning(ex.message);
    }
};

const reloadData = async (data: any, blueprint?: any, tableRowIndex?: number | null) =>
{
    Object.keys(data).forEach((key) =>
    {
        Object.keys(entries.value).forEach((entryKey) =>
        {
            const entry = entries.value[entryKey];

            if (entry.hasOwnProperty(key))
            {
                const currentBlueprint = builders.value[entryKey].schema.find(key) as EntryFactory<any>;

                if (!currentBlueprint) return;

                entry[key] = currentBlueprint.createEntry(data[key].data);
            }
        });
    });

    Object.values(builders.value).forEach((builder) =>
    {
        builder?.update();
    });
};

const reloadDataWithoutReloadForm = async (data: any, blueprint?: any, tableRowIndex?: number | null) =>
{
    if (data === true)
    {
        const guid = guids.value?.[0];
        const builder = guid ? builders.value[guid] : null;

        if (!builder) return;

        builder.update();

        return;
    }

    Object.keys(data).forEach((key) =>
    {
        Object.keys(entries.value).forEach((entryKey) =>
        {
            const entry = entries.value[entryKey];

            if (entry.hasOwnProperty(key))
            {
                const currentBlueprint = builders.value[entryKey].schema.find(key) as EntryFactory<any>;

                if (!currentBlueprint) return;

                const oldEntry = JSON.parse(JSON.stringify(entry[key]));

                entry[key] = currentBlueprint.updateEntry(data[key].data, oldEntry);
                useControlReload($events, key, props.level).emitReload(tableRowIndex);

                emitBlueprintChanges($events, builders.value[entryKey].blueprint.id, blueprint, currentBlueprint, entry[key], oldEntry, tableRowIndex);
            }
        });
    });
};

const addEntriesData = () =>
{
    Object.keys(entries.value).forEach((entryKey) =>
    {
        entries.value[entryKey] = builders.value[entryKey].getEntry() as FormEntry;
        entries.value[entryKey].module = action.licence;
        entries.value[entryKey].formId = `${entryKey ? `${entryKey}/` : ''}${action.buttonId}`;
        entries.value[entryKey].actionName = action.actionName;
    });
};

const addEvents = () =>
{
    $events.$on(`${FrontActionModal.Object}-${props.level}`, async (_action: BpmnEmitOptions) =>
    {
        form.document = null;
        entries.value = {};
        builders.value = {};
        action.withData(_action);

        try
        {
            if (!action.buttonId)
            {
                $alert.warning($t('[[[Nie podano ID procesu, którego dotyczy formularz]]]'));

                return;
            }

            await initForm();

            modal.value.show();
        }
        catch (ex: any)
        {
            if (ex.code === 400) $alert.warning(ex.message);
        }
    });

    $events.$on(
        `update-related-documents-form-${props.level}`,
        async (blueprint: RelatedDocumentContract, publicId: string, eventAction?: BpmnEmitOptions) =>
        {
            const pager = new Pager(1, 32, '', 'ASC');
            const response = await ModulesService.fetchDocument(
                action.licence,
                entries.value[guids.value[0]].formId,
                action.actionName,
                blueprint.name,
                { key: publicId },
                pager
            );
            const document = response.items?.[0]?.result;

            if (blueprint && document)
            {
                let relatedControlEntry = entries.value[guids.value[0]][blueprint.name];

                if (eventAction.parent?.type === 'table')
                    relatedControlEntry = entries.value[guids.value[0]][eventAction.parent.name].data[eventAction.index][blueprint.name];

                if (blueprint.multiselect)
                {
                    relatedControlEntry.data ??= [];
                    relatedControlEntry.data.push(document);
                }
                else relatedControlEntry.data = Array.isArray(document) ? document : [document];
            }

            $events.$emit(
                `update-related-documents-${props.level}`,
                blueprint,
                publicId
            );
        }
    );

    // $events.$on(`file-to-preview-${props.level}`, (file: FileResource) =>
    // {
    //     filePreview.value = file;
    // });

    $events.$on(`file-to-preview-${props.level}`, (file: FileResource, documentControlName?: string) =>
    {
        if (!file) return;

        const docControlName = documentControlName ?? '';

        const idx = filePreview.value.findIndex(
            (p) => p.documentControlName === (docControlName)
        );

        if (idx !== -1)
        {
            filePreview.value[idx] = { file, documentControlName: docControlName };
        }
        else
        {
            filePreview.value.push({ file, documentControlName: docControlName });
        }
    });

    $events.$on('file-to-remove', (file: FilePreview) =>
    {
        if (!file) return;

        filePreview.value =  filePreview.value.filter((p) => p.file.publicId !== file.file.publicId);
    });

    $events.$on('form-reload', () =>
    {
        const guid = guids.value?.[0];
        const builder = guid ? builders.value[guid] : null;

        if (!builder) return;

        builder.update();
        reloadKey.value++;
    });
};

const addFormEvents = () =>
{
    $events.$on(`form-reload-data-${builders.value[guids.value[0]].blueprint.id}`, (data: any) =>
    {
        reloadData(data);
        reloadKey.value++;
    });

    const fullId = guids.value ? guids.value + '/' + action.buttonId : action.buttonId;

    useDynamicTrigger($events, builders.value[guids.value[0]].blueprint.id).eventOn(builders.value[guids.value[0]], builders.value[guids.value[0]], action.licence, fullId, reloadDataWithoutReloadForm, $alert, entries.value[guids.value?.[0] ?? ''], formObj, props);
};

const onExecCompleted = () =>
{
    isExecuting.value = false;

    $alert.success($t('[[[Akcja wykonana pomyślnie]]]'));

    setTimeout(() =>
    {
        emit('reload');
        $events.$emit('refetch');
        $events.$emit('refetchActions');
        $events.$emit('refetchRelatedDocuments');
    }, 500);
};

const onExecError = (ex: any) =>
{
    isExecuting.value = false;

    if (ex.code == 400)
    {
        $alert.warning(ex.message);
    }
};

const onSubmit = async () =>
{
    form.wait();

    const additonalData: Record<string, any> = {};
    let data: Record<string, any> = {};

    try
    {
        for (const docId of Object.keys(builders.value))
        {
            const builder = builders.value[docId];

            builder.setEntryErrors({});

            const d = (await builder.collectEntry(async (blueprint: Blueprint, entry: Entry) =>
            {
                instanceOfAttachmentsEntry(entry) && (await entry.upload(blueprint as AttachmentsContract, async (file) => await StorageService.upload(file, 'documents')));
                instanceOfUserGroupEntry(entry) && (additonalData[`${blueprint.name}_type`] = entry.settings?.key);
                instanceOfUserEntry(entry) && (additonalData[`${blueprint.name}_type`] = entry.valuesType);
            })) as Record<string, any>;

            if (action.multiformMode)
            {
                data[docId] = { ...d, ...additonalData };
            }
            else
            {
                data = d;
            }
        }
    }
    catch (ex: any)
    {
        $alert.error($t(ex.message));

        return;
    }

    if (!action.multiformMode)
    {
        data = { ...data, ...additonalData };
    }

    try
    {
        if (action.id)
        {
            // Group action
            if (action.groupAction)
            {
                const endpoint = `${action.licence}/${action.buttonId}/bpmn-execute/group`;

                data._publicIds = action.id.split(',');

                const response: any = await BpmnService.execute(endpoint, data);

                executionStateId.value = response;
                isExecuting.value = true;

                nextTick(() => exec.value?.start(response, 500));
            }
            else
            {
                const endpoint = `${action.licence}/${action.id}/${action.buttonId}/bpmn-execute`;

                await BpmnService.execute(endpoint, data, isSubprocess.value);
                $alert.success($t('[[[Zapisano formularz]]]'));

                modal.value.hide();
                reset();

                setTimeout(() =>
                {
                    emit('reload');
                    $events.$emit('refetch');
                    // $events.$emit('refetchData');
                    $events.$emit('refetchActions');
                    $events.$emit('refetchRelatedDocuments');
                }, 500);
            }
        }
        else
        {
            const response = await ModulesService.saveForm(action.licence, action.buttonId, data, isSubprocess.value);
            const publicId = response.result.publicId;

            await new Promise((resolve) =>
                setTimeout(() =>
                {
                    $events.$emit(
                        `update-related-documents-form-${props.level - 1}`,
                        action.blueprint,
                        publicId,
                        action
                    );
                    resolve('resolved');
                }, 1000)
            );

            $alert.success($t('[[[Zapisano formularz]]]'));

            modal.value.hide();
            reset();
            setTimeout(() =>
            {
                emit('reload');
                $events.$emit('refetch');
                // $events.$emit('refetchData');
                $events.$emit('refetchActions');
                $events.$emit('refetchRelatedDocuments');
            }, 500);
        }
    }
    catch (ex: any)
    {
        if (ex.code === 400) $alert.danger($t('[[[Nie udało się zapisać formularza]]]'));
        else if (ex.code === 422)
        {
            const errors: Record<string, string[]> = {};

            Object.entries(ex.data.errors).forEach(([key, value]) =>
            {
                if (key.toLowerCase() === 'registerid')
                {
                    const registerControl = Object.values(builders.value)
                        .flatMap(b => b.schema.components('register'))
                        .at(0);

                    if (registerControl)
                        errors[`${registerControl.name.toLowerCase()}.value`] = value as string[];

                    return;
                }

                const keyWithDot =
                    key.includes('.value') || key.includes('.custom')
                        ? key.toLowerCase()
                        : `${key.toLowerCase()}.value`;

                errors[keyWithDot] = value as string[];
            });

            if (action.multiformMode)
            {
                for (const docId of Object.keys(builders.value))
                {
                    const filteredErrors = Object.keys(errors).filter(p => p.startsWith(docId));

                    const singleFormErrors: Record<string, string[]> = {};

                    filteredErrors.forEach(p =>
                    {
                        singleFormErrors[p.substring(docId.length + 1)] = errors[p];
                    });

                    builders.value[docId].setEntryErrors(singleFormErrors);
                }
            }
            else
            {
                builders.value[guids.value[0]].setEntryErrors(errors);
            }

            $alert.warning($t('[[[Nie wszystkie pola zostały wypełnione prawidłowo.]]]'));
        }
    }
    finally
    {
        if (!isExecuting.value)
        {
            form.continue();
        }
    }
};

const initForm = async () =>
{
    form.wait();
    await loadForm();
    await loadData();
    addEntriesData();
    addFormEvents();

    isLoaded.value = true;
    form.complete();
};

const init = async () =>
{
    addEvents();
};

init();

onUnmounted(() =>
{
    $events.$off(`${FrontActionModal.Object}-${props.level}`);
    $events.$off(`update-related-documents-form-${props.level}`);
    $events.$off(`file-to-preview-${props.level}`);

    if (exec.value) exec.value.stop();
});

const onModalShown = async () =>
{
    if (guids.value.length > 1) return;

    const token = !action.id
        ? await ModulesService.initFormSession(action.licence, action.buttonId)
        : await ModulesService.initEditFormSession(action.licence, action.buttonId, action.id);

    if (token)
        axios.defaults.headers.common['FormSessionId'] = token;
};

const onModalHidden = async () =>
{
    reset();
    delete axios.defaults.headers.common['FormSessionId'];
};
</script>

<template>
    <IdeoModal
        ref="modal"
        centered
        :size="size"
        :title="title"
        @close="reset"
        @hidden="onModalHidden"
        @shown="onModalShown"
    >
        <template #default>
            <div v-if="executionStateId">
                <GroupExecutionProgress ref="exec" :licence="action.licence" @completed="onExecCompleted" @error="onExecError" />
            </div>
            <div class="row">
                <div :class="isLogitoDocument ? 'col-md-6' : 'col-md-12'">
                    <div v-if="!isLoaded" class="position-absolute top-50 start-50 translate-middle">
                        <Loader />
                    </div>
                    <form v-else id="dynamic-form" ref="formObj" @submit.prevent="onSubmit">
                        <div v-for="(builder, index) in builders" :key="index" :class="action.multiformMode ? 'mb-4 p-3 border rounded' : ''">
                            <h6 v-if="action.multiformMode" class="mb-3">{{ documentTitles[index] }}</h6>
                            <FormBlueprint :key="reloadKey" :builder="builder" />
                        </div>
                    </form>
                </div>
                <div v-if="isLogitoDocument" class="col-md-6">
                    <FileReader v-if="filePreview && filePreview.url" :src="filePreview.url" />
                </div>
            </div>
            <BPMNDynamicModal :level="level + 1" />
        </template>
        <template #modal-footer="{ cancel }">
            <IdeoButton
                :variant="action.buttonVariant"
                :disabled="!form.active() || isExecuting"
                @click="onSubmit"
            >
                <i v-if="action.buttonIcon" class="me-2" :class="action.buttonIcon"></i>
                <span v-if="isExecuting">{{ $t('[[[Wykonywanie...]]]') }}</span>
                <span v-else>{{ action.formButtonName || action.buttonName }}</span>
            </IdeoButton>
            <IdeoButton variant="secondary" :disabled="isExecuting" @click="cancel">
                {{ executionStateId ? $t('[[[Zamknij]]]') : $t('[[[Anuluj]]]') }}
            </IdeoButton>
        </template>
    </IdeoModal>
</template>
